<template>
    <Modal
        :open="open"
        :title="title"
        :width="width"
        :footer="null"
        @update:open="$emit('update:open', $event)"
        @cancel="$emit('cancel')"
    >
        <div v-if="data" class="detail-content">
            <!-- 基本資訊區塊 -->
            <div v-if="basicInfo.length > 0" class="info-section">
                <a-descriptions :column="columnCount" bordered>
                    <a-descriptions-item
                        v-for="item in basicInfo"
                        :key="item.label"
                        :label="item.label"
                    >
                        <span
                            v-if="item.type === 'status'"
                            class="status-text"
                            :class="
                                item.value === 1 || item.value === 'active'
                                    ? 'active'
                                    : 'inactive'
                            "
                        >
                            {{
                                item.value === 1 || item.value === 'active'
                                    ? '啟用'
                                    : '停用'
                            }}
                        </span>
                        <span
                            v-else-if="item.type === 'level'"
                            class="level-text"
                        >
                            {{ item.value }}
                        </span>
                        <span
                            v-else-if="item.type === 'points'"
                            class="points-value"
                        >
                            {{ item.value }}
                        </span>
                        <span v-else>{{ item.value }}</span>
                    </a-descriptions-item>
                </a-descriptions>
            </div>

            <!-- 分隔線 -->
            <a-divider v-if="basicInfo.length > 0 && additionalContent" />

            <!-- 額外內容區塊 -->
            <div v-if="additionalContent" class="additional-content">
                <slot name="additional"></slot>
            </div>

            <!-- 權限列表 (角色管理用) → 樹狀唯讀呈現，允許在未傳入 permissions 時用全量權限渲染 -->
            <div
                v-if="
                    showPermissions &&
                    permissionTreeData &&
                    (permissionTreeData.length > 0 || open)
                "
                class="permissions-section"
            >
                <a-divider />
                <div class="perm-panel">
                    <div class="perm-tree-toolbar">
                        <a-space wrap size="small">
                            <a-switch
                                v-model:checked="expandedAll"
                                size="small"
                                :checked-children="'展開'"
                                :un-checked-children="'收合'"
                            />
                            <a-input-search
                                v-model:value="permSearch"
                                placeholder="搜尋權限名稱或代碼"
                                allow-clear
                                class="perm-search"
                            />
                        </a-space>
                        <span class="perm-summary">
                            已授權 {{ grantedPermissionCount }} /
                            {{ totalPermissionCount }}
                        </span>
                    </div>
                    <div v-if="permissionTreeData.length" class="perm-scroll">
                        <a-collapse
                            v-model:activeKey="activeModuleKeys"
                            :bordered="false"
                            class="perm-collapse"
                        >
                            <a-collapse-panel
                                v-for="root in filteredTreeData"
                                :key="root.key"
                                class="perm-module-panel"
                            >
                                <template #header>
                                    <div class="perm-module-header">
                                        <span class="perm-module-title">
                                            {{ root.title }}
                                        </span>
                                        <span
                                            class="perm-module-count"
                                            :class="{
                                                'is-complete':
                                                    isModuleFullyGranted(root)
                                            }"
                                        >
                                            {{ countGrantedInModule(root) }}/{{
                                                countTotalInModule(root)
                                            }}
                                        </span>
                                    </div>
                                </template>
                                <a-tree
                                    checkable
                                    class="perm-tree perm-tree-readonly"
                                    :tree-data="treeNodesForModuleRoot(root)"
                                    :field-names="{
                                        title: 'title',
                                        key: 'key',
                                        children: 'children'
                                    }"
                                    :expanded-keys="expandedKeys"
                                    :checked-keys="
                                        checkedKeysByModule[root.key] ||
                                        emptyTreeCheckState
                                    "
                                    :checkStrictly="true"
                                    :selectable="false"
                                    :show-line="false"
                                    block-node
                                    @check="() => {}"
                                    @expand="(keys) => (expandedKeys = keys)"
                                />
                            </a-collapse-panel>
                        </a-collapse>
                    </div>
                </div>
            </div>

            <!-- 自定義內容插槽 -->
            <slot name="content"></slot>
        </div>
    </Modal>
</template>

<script setup>
import Modal from './Modal.vue';
import { computed, ref, watch } from 'vue';
import { getPermissions } from '@/api/roles';
import {
    normalizeSelectionWithGroupParents,
    computeTreeCheckState,
    treeNodesForModuleRoot
} from '@/utils/permissionTree';

const emptyTreeCheckState = { checked: [], halfChecked: [] };

// Props
const props = defineProps({
    open: {
        type: Boolean,
        default: false
    },
    title: {
        type: String,
        default: '詳情'
    },
    width: {
        type: [String, Number],
        default: '700px'
    },
    data: {
        type: Object,
        default: null
    },
    basicInfo: {
        type: Array,
        default: () => []
    },
    permissions: {
        type: Array,
        default: () => []
    },
    columnCount: {
        type: Number,
        default: 1
    },
    additionalContent: {
        type: Boolean,
        default: false
    },
    showPermissions: {
        type: Boolean,
        default: false
    }
});

// Emits
const emit = defineEmits(['update:open', 'cancel']);

// 使用後端提供的 permissions.children 直接建樹
const buildPermissionTree = (list = []) => {
    const permissions = Array.isArray(list) ? list : [];
    const toTreeNode = (node, isGroup = false) => ({
        key: `perm-${node.id}`,
        title: node.name,
        code: String(node.code),
        isGroup,
        disableCheckbox: true,
        children: Array.isArray(node.children)
            ? node.children.map((c) => toTreeNode(c, false))
            : undefined
    });
    // 後端已提供 children，直接映射為樹
    return permissions.map((g) => toTreeNode(g, true));
};

// 2 新的物件版權限樹（以模組名稱為 key，內含 permissions 陣列）
const resolveTreeNodeKey = (item) => {
    if (item.tree_key) return item.tree_key;
    const hasChildren =
        Array.isArray(item.permissions) && item.permissions.length > 0;
    if (hasChildren && item.code) return `group-${item.code}`;
    return item.code;
};

const toTreeFromArray = (arr = []) =>
    (arr || []).map((item) => {
        const nested = Array.isArray(item.permissions)
            ? item.permissions
            : null;
        return {
            key: resolveTreeNodeKey(item),
            code: item.code || null,
            title: item.name,
            disableCheckbox: true,
            children: nested?.length ? toTreeFromArray(nested) : undefined
        };
    });

const toTreeFromMap = (obj = {}) =>
    Object.values(obj || {}).map((mod) => ({
        key: mod.code,
        code: mod.code,
        title: mod.name,
        disableCheckbox: true,
        children: Array.isArray(mod.permissions)
            ? toTreeFromArray(mod.permissions)
            : undefined
    }));

// 判斷 props.permissions 是否為物件版（模組名為 key）
const isObjectGrouped = computed(
    () =>
        !Array.isArray(props.permissions) &&
        typeof props.permissions === 'object' &&
        props.permissions
);

// 取得全系統權限，並與擁有權限比對（以 code 為唯一鍵）
// 必須在 permissionTreeData 之前定義，因為 permissionTreeData 會使用它
const allPermissions = ref([]);

// Tree 資料來源優先序：
// 1 若父層已提供 props.permissions（陣列）→ 檢查是否為階層結構
// 2 若 props.permissions 是物件分群 → 直接用
// 3 若 props.permissions 是扁平陣列 → 使用全量 /v1/permissions 的階層結構，用傳入的 code 勾選
// 4 否則退回用全量 /v1/permissions
const permissionTreeData = computed(() => {
    // 1 父層直接給了陣列
    if (Array.isArray(props.permissions) && props.permissions.length > 0) {
        const first = props.permissions[0] || {};
        // a 已是 children 階層
        if (Array.isArray(first.children))
            return buildPermissionTree(props.permissions);
        // b 模組陣列，子節點在 permissions 欄位
        if (Array.isArray(first.permissions))
            return toTreeFromArray(props.permissions);
        // c 純扁平（只有葉子/僅 code/name）→ 使用全量權限的階層結構
        const ap = allPermissions.value;
        if (ap) {
            if (Array.isArray(ap)) {
                const f = ap[0] || {};
                return Array.isArray(f.children)
                    ? buildPermissionTree(ap)
                    : toTreeFromArray(ap);
            }
            if (typeof ap === 'object') return toTreeFromMap(ap);
        }
        // 如果全量權限還沒載入，先用扁平陣列建樹（臨時顯示）
        return toTreeFromArray(props.permissions);
    }
    // 2 物件分群
    if (isObjectGrouped.value) return toTreeFromMap(props.permissions);
    // 3 退回全量 /v1/permissions
    const ap = allPermissions.value;
    if (!ap) return [];
    if (Array.isArray(ap)) {
        const f = ap[0] || {};
        return Array.isArray(f.children)
            ? buildPermissionTree(ap)
            : toTreeFromArray(ap);
    }
    if (typeof ap === 'object') return toTreeFromMap(ap);
    return [];
});

// 展開/收合與 keys 計算
const flattenKeys = (arr = [], out = []) => {
    arr.forEach((n) => {
        out.push(n.key);
        if (n.children) flattenKeys(n.children, out);
    });
    return out;
};

// 僅勾選「實際擁有的代碼」對應的節點（不因為是物件分群就全選）
const collectCheckedKeys = (arr = [], out = []) => {
    arr.forEach((n) => {
        if (n.code && isOwned(n.code)) out.push(n.key);
        if (n.children) collectCheckedKeys(n.children, out);
    });
    return out;
};

const baseCheckedKeys = computed(() =>
    collectCheckedKeys(permissionTreeData.value)
);

/** 與編輯模式一致：子頁全授權時補勾 group-system 等無 code 群組節點 */
const checkedKeys = computed(() =>
    normalizeSelectionWithGroupParents(
        permissionTreeData.value,
        baseCheckedKeys.value
    )
);

/** 檢視模式：父節點子孫未全選時顯示半選「-」 */
const checkedKeysByModule = computed(() => {
    const selected = new Set(checkedKeys.value);
    const out = {};
    permissionTreeData.value.forEach((root) => {
        const moduleKeys = new Set(flattenKeys([root]));
        const moduleSelected = [...moduleKeys].filter((k) => selected.has(k));
        out[root.key] = computeTreeCheckState(root, moduleSelected, moduleKeys);
    });
    return out;
});

// 展開控制
const expandedAll = ref(true);
const expandedKeys = ref([]);
const activeModuleKeys = ref([]);
const permSearch = ref('');

const allModuleKeys = computed(() =>
    permissionTreeData.value.map((r) => r.key)
);

const totalPermissionKeys = computed(() =>
    flattenKeys(permissionTreeData.value)
);

const totalPermissionCount = computed(() => totalPermissionKeys.value.length);

const grantedPermissionCount = computed(() => checkedKeys.value.length);

const countTotalInModule = (root) => flattenKeys([root]).length;

const countGrantedInModule = (root) => {
    const moduleKeys = new Set(flattenKeys([root]));
    return checkedKeys.value.filter((k) => moduleKeys.has(k)).length;
};

const isModuleFullyGranted = (root) => {
    const total = countTotalInModule(root);
    if (!total) return false;
    return countGrantedInModule(root) === total;
};

const syncExpandedState = () => {
    if (expandedAll.value) {
        activeModuleKeys.value = [...allModuleKeys.value];
        expandedKeys.value = flattenKeys(permissionTreeData.value);
        return;
    }
    activeModuleKeys.value = [];
    expandedKeys.value = [];
};

const computeAllExpandKeys = () => flattenKeys(permissionTreeData.value);

watch(
    () => [permissionTreeData.value, expandedAll.value],
    () => {
        if (!permSearch.value.trim()) {
            syncExpandedState();
        }
    },
    { immediate: true }
);

watch(permSearch, () => {
    const q = permSearch.value.trim();
    if (!q) {
        syncExpandedState();
        return;
    }
    activeModuleKeys.value = filteredTreeData.value.map((r) => r.key);
});

// 搜尋過濾（名稱或代碼包含）
const filteredTreeData = computed(() => {
    const q = permSearch.value.trim().toLowerCase();
    if (!q) return permissionTreeData.value;

    const filter = (nodes = []) =>
        nodes
            .map((n) => {
                const title = String(n.title || '').toLowerCase();
                const hit =
                    title.includes(q) || String(n.code || '').includes(q);
                const children = n.children ? filter(n.children) : undefined;
                if (hit || (children && children.length)) {
                    return { ...n, children };
                }
                return null;
            })
            .filter(Boolean);

    // 自動展開匹配節點路徑
    const keys = flattenKeys(filter(permissionTreeData.value));
    expandedKeys.value = keys;
    return filter(permissionTreeData.value);
});

// 擁有權限代碼集合
const ownedCodes = computed(() => {
    // 來源可能是：
    // - 扁平陣列：[{ code }...]
    // - 巢狀陣列：[{ code, children:[...] }...]
    // - 放在 data.permissions
    const source = Array.isArray(props.permissions)
        ? props.permissions
        : Array.isArray(props.data?.permissions)
          ? props.data.permissions
          : [];

    const flattenCodes = (list = [], acc = []) => {
        list.forEach((item) => {
            if (item && item.code != null) acc.push(String(item.code));
            if (Array.isArray(item.children)) flattenCodes(item.children, acc);
            if (Array.isArray(item.permissions))
                flattenCodes(item.permissions, acc);
        });
        return acc;
    };

    return new Set(flattenCodes(source));
});

const isOwned = (code) => ownedCodes.value.has(String(code));

const isPermissionsEmpty = (val) => {
    if (!val) return true;
    if (Array.isArray(val)) return val.length === 0;
    if (typeof val === 'object') return Object.keys(val).length === 0;
    return true;
};

const loadAllPermissions = async () => {
    try {
        const res = await getPermissions();
        if (res.result) allPermissions.value = res.data || [];
    } catch (e) {
        // 靜默失敗，維持空陣列
    }
};

// 僅在 Modal 開啟時才載入全量權限，避免頁面初始多次請求
watch(
    () => props.open,
    (isOpen) => {
        if (isOpen && isPermissionsEmpty(allPermissions.value)) {
            loadAllPermissions();
        }
    },
    { immediate: true }
);

// 移除未使用的 allModules 與行內展平輔助，避免維護負擔
</script>

<style scoped>
.detail-content {
    color: #2a2a3a;
    font-family: 'Courier New', monospace;
}

.info-section {
    margin-bottom: 16px;
}

.additional-content {
    margin-top: 16px;
}

.permissions-section {
    margin-top: 24px;
}

.permission-item {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    background: rgba(42, 74, 122, 0.1);
    border-radius: 8px;
    border: 1px solid rgba(200, 200, 210, 0.4);
    margin-bottom: 8px;
}

/* === 檢視 Modal：手風琴模組權限樹 === */
.perm-panel {
    border: 1px solid rgba(42, 74, 122, 0.25);
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.92);
    overflow: hidden;
}

.perm-tree-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 10px 12px;
    border-bottom: 1px solid rgba(42, 74, 122, 0.15);
    background: rgba(248, 245, 255, 0.65);
}

.perm-search {
    width: 240px;
}

.perm-summary {
    flex-shrink: 0;
    font-size: 13px;
    font-weight: 600;
    color: #6a4a8a;
    white-space: nowrap;
}

.perm-scroll {
    max-height: min(58vh, 520px);
    overflow-y: auto;
    padding: 8px 10px 10px;
}

.perm-collapse :deep(.ant-collapse-item) {
    border: 1px solid rgba(42, 74, 122, 0.18) !important;
    border-radius: 10px !important;
    margin-bottom: 8px;
    overflow: hidden;
    background: #fff;
}

.perm-collapse :deep(.ant-collapse-item:last-child) {
    margin-bottom: 0;
}

.perm-collapse :deep(.ant-collapse-header) {
    padding: 8px 12px !important;
    align-items: center !important;
    background: rgba(248, 245, 255, 0.45);
}

.perm-collapse :deep(.ant-collapse-content-box) {
    padding: 4px 8px 8px !important;
}

.perm-module-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    width: 100%;
    padding-right: 4px;
}

.perm-module-title {
    font-weight: 600;
    color: #2a2a3a;
}

.perm-module-count {
    min-width: 44px;
    padding: 0 8px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 600;
    line-height: 22px;
    text-align: center;
    color: #6a6a7a;
    background: rgba(42, 74, 122, 0.12);
}

.perm-module-count.is-complete {
    color: #fff;
    background: #2a4a7a;
}

.perm-tree :deep(.ant-tree-treenode) {
    padding: 1px 0;
}

.perm-tree :deep(.ant-tree-indent-unit) {
    width: 14px;
}

.perm-tree :deep(.ant-tree-node-content-wrapper) {
    min-height: 26px;
    line-height: 26px;
    border-radius: 6px;
}

.perm-tree :deep(.ant-tree-switcher) {
    width: 20px;
}

.perm-tree-readonly
    :deep(.ant-tree-checkbox-disabled .ant-tree-checkbox-inner) {
    cursor: default;
    opacity: 1 !important;
}

.perm-tree-readonly
    :deep(
        .ant-tree-checkbox-disabled.ant-tree-checkbox-checked
            .ant-tree-checkbox-inner
    ) {
    background-color: #245a8c !important;
    border-color: #1a4268 !important;
}

/* 權限樹 checkbox（唯讀檢視也維持清楚邊框與勾勾） */
.perm-tree :deep(.ant-tree-checkbox-inner) {
    width: 16px !important;
    height: 16px !important;
    background: #ffffff !important;
    border: 2px solid #245a8c !important;
    border-radius: 3px !important;
}

.perm-tree :deep(.ant-tree-checkbox-checked .ant-tree-checkbox-inner) {
    background-color: #245a8c !important;
    border-color: #1a4268 !important;
}

.perm-tree :deep(.ant-tree-checkbox-indeterminate .ant-tree-checkbox-inner) {
    background-color: #d4b3ff !important;
    border-color: #245a8c !important;
}

.perm-tree :deep(.ant-tree-checkbox-checked .ant-tree-checkbox-inner::after),
.perm-tree
    :deep(.ant-tree-checkbox-indeterminate .ant-tree-checkbox-inner::after) {
    border-color: #ffffff !important;
    border-width: 0 2px 2px 0 !important;
    opacity: 1 !important;
}

/* 被選節點文字更明顯 */
:deep(.ant-tree-checkbox-checked + .ant-tree-node-content-wrapper) {
    color: #2a2a3a !important;
    font-weight: 700 !important;
    text-shadow: 0 0 8px rgba(42, 74, 122, 0.25);
}

.permission-name {
    color: #2a2a3a;
    font-family: 'Courier New', monospace;
    font-size: 14px;
}

.status-text {
    font-family: 'Courier New', monospace;
    font-weight: 500;
}

.status-text.active {
    color: #d0ffb7;
    /* 薄荷綠 - 啟用 */
}

.status-text.inactive {
    color: #ffa87a;
    /* 日落橘 - 停用 */
}

.level-text {
    color: #2a2a3a;
    font-family: 'Courier New', monospace;
    font-weight: 500;
}

.points-value {
    font-weight: 600;
    color: #0e7490;
    font-family: 'Courier New', monospace;
}

/* Descriptions 樣式 */
:deep(.ant-descriptions) {
    background: transparent !important;
}

:deep(.ant-descriptions-item-label) {
    color: #2a2a3a !important;
    font-family: 'Courier New', monospace !important;
    font-weight: 600 !important;
    background: rgba(255, 255, 255, 0.92) !important;
    border-color: rgba(200, 200, 210, 0.4) !important;
}

:deep(.ant-descriptions-item-content) {
    color: #2a2a3a !important;
    font-family: 'Courier New', monospace !important;
    background: rgba(255, 255, 255, 0.88) !important;
    border-color: rgba(200, 200, 210, 0.4) !important;
}

/* Divider 樣式 */
:deep(.ant-divider) {
    border-color: rgba(200, 200, 210, 0.4) !important;
}

:deep(.ant-divider-inner-text) {
    color: #2a2a3a !important;
    /* 調深提升可讀性 */
    font-family: 'Courier New', monospace !important;
    font-weight: 700 !important;
    background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.98),
        rgba(248, 249, 250, 0.95)
    ) !important;
}

/* Tag 樣式 */
:deep(.ant-tag) {
    background: rgba(138, 175, 255, 0.2) !important;
    border: 1px solid rgba(138, 175, 255, 0.5) !important;
    color: #3b6ea5 !important;
    font-family: 'Courier New', monospace !important;
    border-radius: 50px !important;
    padding: 4px 12px !important;
}

:deep(.ant-tag-purple) {
    background: rgba(42, 74, 122, 0.2) !important;
    border-color: #2a4a7a !important;
    color: #2a4a7a !important;
}

:deep(.ant-tag-green),
:deep(.ant-tag-success) {
    background: rgba(82, 196, 26, 0.2) !important;
    border-color: #52c41a !important;
    color: #52c41a !important;
}

:deep(.ant-tag-red),
:deep(.ant-tag-error) {
    background: rgba(255, 77, 79, 0.2) !important;
    border-color: #ff4d4f !important;
    color: #ff4d4f !important;
}

:deep(.ant-tag-blue),
:deep(.ant-tag-processing) {
    background: rgba(138, 175, 255, 0.2) !important;
    border-color: #3b6ea5 !important;
    color: #3b6ea5 !important;
}

:deep(.ant-tag-cyan) {
    background: rgba(230, 247, 255, 0.2) !important;
    border-color: #17a2b8 !important;
    color: #17a2b8 !important;
}

:deep(.ant-tag-orange),
:deep(.ant-tag-warning) {
    background: rgba(255, 168, 122, 0.2) !important;
    border-color: #ffa87a !important;
    color: #ffa87a !important;
}

:deep(.ant-tag-default) {
    background: rgba(255, 255, 255, 0.8) !important;
    border-color: rgba(200, 200, 210, 0.4) !important;
    color: #2a2a3a !important;
}

/* 響應式設計 */
@media (max-width: 768px) {
    .permission-item {
        flex-direction: column;
        align-items: flex-start;
        gap: 4px;
    }

    .permissions-grid :deep(.ant-col) {
        margin-bottom: 8px;
    }
}

/* 欄式呈現（類似你截圖的分欄群組） */
.permissions-columns {
    margin-top: 8px;
}

.perm-column-card {
    background: rgba(255, 255, 255, 0.9);
    border: 1px solid rgba(42, 74, 122, 0.35);
    border-radius: 12px;
    padding: 12px 14px;
    box-shadow: 0 6px 20px rgba(13, 13, 13, 0.06);
}

.perm-column-header {
    background: linear-gradient(45deg, #64748b, #2a4a7a, #3b6ea5);
    color: #0d0d0d;
    font-weight: 700;
    padding: 6px 10px;
    border-radius: 8px;
    margin-bottom: 10px;
}

.perm-feature {
    margin-bottom: 8px;
}

.perm-feature-title {
    color: #2a2a3a;
    font-weight: 600;
}

.perm-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 6px 14px;
    margin-top: 6px;
}

.perm-actions :deep(.ant-checkbox-wrapper) {
    color: #2a2a3a !important;
}

.perm-actions :deep(.ant-checkbox-checked .ant-checkbox-inner) {
    background-color: #3b6ea5 !important;
    border-color: #3b6ea5 !important;
}

.code-text {
    color: rgba(42, 42, 58, 0.55);
    margin-left: 4px;
}

/* 簡易列表：只顯示 children 清單（完全依後端） */
.perm-list-simple {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.perm-list-item :deep(.ant-checkbox-inner) {
    background-color: #3b6ea5 !important;
    border-color: #3b6ea5 !important;
}

/* 標題與縮排層級 */
.perm-heading {
    color: #2a2a3a;
    font-weight: 700;
    margin-top: 8px;
}

.depth-1 {
    padding-left: 12px;
}

.depth-2 {
    padding-left: 24px;
}

/* 移除不再使用的 tree 相關樣式避免干擾 */
.permissions-tree-view {
    display: none;
}
</style>
